import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Grid,
  Input,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsFilterOperator,
  OmsProduct,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import OMSExportPanel from "./OMSExportPanel";
import { columnValueError, comparisonLabel } from "./omsReportFormatting";

type Condition = OmsRecordFilter & { id: number };

export default function OMSRecordFilters({
  report,
  product,
}: {
  report: OmsReport;
  product: OmsProduct;
}) {
  const [criteria, setCriteria] = useState<Condition[]>([
    { id: 0, column: "", operator: OmsFilterOperator.Eq, value: "" },
  ]);
  const [nextId, setNextId] = useState(1);
  const [operation, setOperation] = useState<"and" | "or">("and");
  const [active, setActive] = useState(false);
  const restoreCriteria = useCallback(
    (filters: OmsRecordFilter[], matching: "and" | "or") => {
      setCriteria(filters.map((criterion, id) => ({ ...criterion, id })));
      setNextId(filters.length);
      setOperation(matching);
    },
    []
  );
  function update(id: number, change: Partial<Condition>) {
    setCriteria((previous) =>
      previous.map((criterion) =>
        criterion.id === id ? { ...criterion, ...change } : criterion
      )
    );
  }
  const valid = criteria.every(
    (criterion) =>
      criterion.column &&
      !columnValueError(
        report.columnTypes?.[criterion.column] || "string",
        criterion.operator,
        criterion.value
      )
  );
  return (
    <SpaceBetween size="m">
      <Box>
        Filter rows in this scheduled report by its actual columns. These
        criteria apply only to the filtered ZIP export.
      </Box>
      <FormField label="Match criteria">
        <Select
          disabled={active}
          selectedOption={{
            value: operation,
            label: operation === "and" ? "All criteria" : "Any criterion",
          }}
          options={[
            { value: "and", label: "All criteria" },
            { value: "or", label: "Any criterion" },
          ]}
          onChange={({ detail }) =>
            setOperation(detail.selectedOption.value as "and" | "or")
          }
        />
      </FormField>
      {criteria.map((criterion, index) => {
        const type = report.columnTypes?.[criterion.column] || "string";
        const allowed =
          type === "string"
            ? ["eq", "ne", "contains", "notContains", "isNull", "isNotNull"]
            : ["eq", "ne", "gt", "gte", "lt", "lte", "isNull", "isNotNull"];
        const nullOperator = ["isNull", "isNotNull"].includes(
          criterion.operator
        );
        return (
          <Grid
            key={criterion.id}
            gridDefinition={[
              { colspan: 4 },
              { colspan: 3 },
              { colspan: 4 },
              { colspan: 1 },
            ]}
          >
            <FormField label={`Column ${index + 1}`}>
              <Select
                disabled={active}
                filteringType="auto"
                placeholder="Choose a report column"
                selectedOption={
                  criterion.column
                    ? { value: criterion.column, label: criterion.column }
                    : null
                }
                options={report.columns.map((column) => ({
                  value: column,
                  label: column,
                  description: report.columnTypes?.[column] || "text",
                }))}
                onChange={({ detail }) =>
                  update(criterion.id, {
                    column: detail.selectedOption.value || "",
                    operator: OmsFilterOperator.Eq,
                    value: "",
                  })
                }
              />
            </FormField>
            <FormField label={`Comparison ${index + 1}`}>
              <Select
                disabled={active}
                selectedOption={{
                  value: criterion.operator,
                  label: comparisonLabel(criterion.operator, type),
                }}
                options={allowed.map((value) => ({
                  value,
                  label: comparisonLabel(value as OmsFilterOperator, type),
                }))}
                onChange={({ detail }) =>
                  update(criterion.id, {
                    operator: detail.selectedOption.value as OmsFilterOperator,
                    value: ["isNull", "isNotNull"].includes(
                      detail.selectedOption.value || ""
                    )
                      ? undefined
                      : "",
                  })
                }
              />
            </FormField>
            <FormField
              label={`Value ${index + 1}`}
              errorText={
                criterion.value
                  ? columnValueError(type, criterion.operator, criterion.value)
                  : undefined
              }
              constraintText={
                nullOperator
                  ? "No value required"
                  : {
                      string: "Text comparisons preserve the original values.",
                      number: "Use a number, for example 100.25.",
                      date: "Use YYYY-MM-DD.",
                      datetime: "Use an ISO date and time.",
                    }[type]
              }
            >
              <Input
                value={criterion.value || ""}
                disabled={active || nullOperator || !criterion.column}
                placeholder={
                  {
                    string: "Value",
                    number: "100.25",
                    date: "2026-10-01",
                    datetime: "2026-10-01T00:00:00Z",
                  }[type]
                }
                onChange={({ detail }) =>
                  update(criterion.id, { value: detail.value })
                }
              />
            </FormField>
            <div className="oms-report-download__remove-filter">
              <Button
                iconName="remove"
                ariaLabel={`Remove criterion ${index + 1}`}
                disabled={active || criteria.length === 1}
                onClick={() =>
                  setCriteria((previous) =>
                    previous.filter((item) => item.id !== criterion.id)
                  )
                }
              />
            </div>
          </Grid>
        );
      })}
      <Button
        iconName="add-plus"
        disabled={active || criteria.length >= 20}
        onClick={() => {
          setCriteria((previous) => [
            ...previous,
            {
              id: nextId,
              column: "",
              operator: OmsFilterOperator.Eq,
              value: "",
            },
          ]);
          setNextId(nextId + 1);
        }}
      >
        Add criterion
      </Button>
      <OMSExportPanel
        product={product}
        report={report}
        mode="filtered"
        operation={operation}
        filters={criteria.map(({ id: _id, ...criterion }) => criterion)}
        valid={valid}
        onActiveChange={setActive}
        onCriteriaLoaded={restoreCriteria}
      />
    </SpaceBetween>
  );
}
